import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ArrowRight, ArrowLeft, Target } from "lucide-react";

interface JobWizardStep2Props {
  onNext: (selectedResponsibilities: string[]) => void;
  onBack: () => void;
  responsibilities: string[];
}

export function JobWizardStep2({ onNext, onBack, responsibilities }: JobWizardStep2Props) {
  const [selectedResponsibilities, setSelectedResponsibilities] = useState<string[]>(
    [...responsibilities] // All pre-selected by default
  );

  const handleToggleResponsibility = (responsibility: string) => {
    setSelectedResponsibilities(prev => 
      prev.includes(responsibility)
        ? prev.filter(r => r !== responsibility)
        : [...prev, responsibility]
    );
  };

  const handleNext = () => {
    onNext(selectedResponsibilities);
  };

  return (
    <div className="min-h-screen bg-blue-100 p-6">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg">
        <div className="bg-gradient-to-r from-accent-light/30 to-primary-light/20 border-b border-border shadow-sm rounded-t-lg backdrop-blur-sm">
          <div className="p-6 border-b border-border-light">
            <div className="flex items-center justify-between">
              <div>
                <Badge variant="outline" className="mb-2">Step 2 of 5</Badge>
                <h1 className="flex items-center gap-2 text-xl font-semibold text-foreground">
                  <Target className="w-5 h-5 text-primary" />
                  Select Responsibilities
                </h1>
                <p className="text-muted-foreground">
                  Review and select the responsibilities that align with your experience
                </p>
              </div>
              <Badge variant="secondary" className="text-lg px-3 py-1">
                {selectedResponsibilities.length} / {responsibilities.length} selected
              </Badge>
            </div>
            <div className="mt-4">
              <Progress value={40} className="w-full" />
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="space-y-4 mb-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Job Responsibilities</h3>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setSelectedResponsibilities([...responsibilities])}
                >
                  Select All
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setSelectedResponsibilities([])}
                >
                  Deselect All
                </Button>
              </div>
            </div>
            <p className="text-muted-foreground">
              All responsibilities are pre-selected. Uncheck any that don't match your experience or interests.
            </p>
          </div>

          <div className="grid gap-3">
            {responsibilities.map((responsibility, index) => (
              <div 
                key={index}
                className={`flex items-start gap-3 p-4 rounded-lg border transition-all cursor-pointer hover:border-primary ${
                  selectedResponsibilities.includes(responsibility) 
                    ? 'border-primary bg-primary-light' 
                    : 'border-border-light'
                }`}
                onClick={() => handleToggleResponsibility(responsibility)}
              >
                <Checkbox 
                  checked={selectedResponsibilities.includes(responsibility)}
                  onChange={() => handleToggleResponsibility(responsibility)}
                  className="mt-1"
                />
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      #{index + 1}
                    </Badge>
                  </div>
                  <p className="text-sm leading-relaxed">
                    {responsibility}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-between pt-6 border-t border-border-light mt-8">
            <Button variant="outline" onClick={onBack}>
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <Button 
              variant="gradient" 
              onClick={handleNext}
              disabled={selectedResponsibilities.length === 0}
            >
              Continue to Requirements
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}